import { Box, DialogContent, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { useMutation } from '@apollo/client';
import { useEffect, useState } from 'react';
import { hideNotificationSettingPopUp, updateNotificationSetting } from '../../../store/reducers/notificationsSlice';

import { UpdateUserSettingMutation } from '../../../graphql/mutation/userSettings';
import { AppDispatch, RootState } from '../../../store';
import { hideLoader, showLoader } from '../../../store/reducers/appSlice';
import { openToast } from '../../../store/reducers/toastSlice';
import { CustomSwitch, NotificationSettingModalUI } from './notification-setting-popup-style';

type INotificationPopupProps = {
    getAllNotifications: () => void;
};
const NotificationSettingPopUp = ({ getAllNotifications }: INotificationPopupProps) => {
    const dispatch = useDispatch<AppDispatch>();
    const { t } = useTranslation();
    const { isShowNotificationSettingPopup, show_notifications, allow_notifications, showPopUpPosition } = useSelector(
        (state: RootState) => state?.notifications,
    );
    const [updateNotificationSettingApi, { data, error }] = useMutation(UpdateUserSettingMutation);
    const [formData, setFormData] = useState({
        show_notifications,
        allow_notifications,
    });

    useEffect(() => {
        setFormData({
            show_notifications,
            allow_notifications,
        });
    }, [show_notifications, allow_notifications]);

    useEffect(() => {
        if (data?.updateSettings || data?.updateSettings?.success) {
            dispatch(hideLoader());
            dispatch(
                openToast({
                    show: true,
                    message: data?.updateSettings?.message || t('update_notification_setting_success_msg'),
                    severity: 'success',
                    isAlertOpen: true,
                }),
            );
            dispatch(hideNotificationSettingPopUp());
            dispatch(
                updateNotificationSetting({
                    show_notifications: formData?.show_notifications,
                    allow_notifications: formData?.allow_notifications,
                }),
            );
            getAllNotifications();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    useEffect(() => {
        if (error) {
            openToast({
                show: true,
                message: t('update_notification_setting_failed_msg'),
                severity: 'error',
                isAlertOpen: true,
            });
            dispatch(hideLoader());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [error]);
    const onChangeHandler = (key: string, value: boolean) => {
        setFormData(prevState => ({
            ...prevState,
            [key]: value,
        }));
        dispatch(showLoader());
        updateNotificationSettingApi({ variables: { input: { [key]: value } } });
    };
    const closePopup = () => {
        dispatch(hideNotificationSettingPopUp());
    };

    return (
        <NotificationSettingModalUI
            open={isShowNotificationSettingPopup}
            anchorEl={showPopUpPosition}
            onClose={closePopup}
            anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
            }}
            transformOrigin={{
                vertical: 'top', // opposite of anchorOrigin
                horizontal: 'center', // aligns from user's point of view
            }}
            sx={{
                mt: 2.3,
                borderRadius: '16px',
            }}
        >
            <DialogContent>
                <Box height="100%" width="100%" justifyContent="center" display="flex" flexDirection="column">
                    <Box>
                        <Typography variant="h5" className="header" mt={1}>
                            {t('notification_settings')}
                        </Typography>
                        <Typography variant="subtitle1" mb={1.5} mt={1}>
                            {t('manage_notification_preferences')}
                        </Typography>

                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mb: 2,
                                mt: 4,
                            }}
                        >
                            <Typography variant="subtitle1" className="header">
                                {t('show_in_app_notifications')}
                            </Typography>
                            <CustomSwitch
                                checked={formData?.show_notifications}
                                onChange={() => onChangeHandler('show_notifications', !formData?.show_notifications)}
                                color="success"
                            />
                        </Box>

                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                            }}
                        >
                            <Typography variant="subtitle1" className="header">
                                {t('enable_push_notifications')}
                            </Typography>
                            <CustomSwitch
                                checked={formData?.allow_notifications}
                                onChange={() => onChangeHandler('allow_notifications', !formData?.allow_notifications)}
                                color="success"
                            />
                        </Box>
                    </Box>
                </Box>
            </DialogContent>
        </NotificationSettingModalUI>
    );
};
export { NotificationSettingPopUp };
